import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import ExpiryDatePicker from './ExpiryDatePicker';
import {
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

interface GalleryImage {
  key: string;
  fileName: string;
  url: string;
  size: number;
  lastModified: string;
  caption?: string;
}

const GalleryManager: React.FC = () => {
  const [folders, setFolders] = useState<string[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingCaption, setEditingCaption] = useState<string | null>(null);
  const [captionText, setCaptionText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{type: 'image' | 'folder', item: string} | null>(null);
  const [expiryDate, setExpiryDate] = useState('');

  useEffect(() => {
    loadFolders();
  }, []);

  useEffect(() => {
    loadImages();
  }, [selectedFolder]);

  const loadFolders = async () => {
    try {
      const folderList = await apiService.getGalleryFolders();
      setFolders(folderList);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  const loadImages = async () => {
    setLoading(true);
    try {
      const imageList = await apiService.getGalleryImages(selectedFolder || undefined);
      setImages(imageList);
    } catch (error) {
      console.error('Failed to load images:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await apiService.createGalleryFolder(newFolderName.trim());
      await loadFolders();
      setShowCreateFolder(false);
      setNewFolderName('');
      alert('Folder created successfully!');
    } catch (error) {
      console.error('Failed to create folder:', error);
      alert('Failed to create folder. Please try again.');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      await apiService.uploadGalleryImage(file, selectedFolder || undefined, undefined, expiryDate || undefined);
      await loadImages();
      // Clear file input
      e.target.value = '';
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateCaption = async (imageKey: string) => {
    try {
      await apiService.updateImageCaption(imageKey, captionText);
      await loadImages();
      setEditingCaption(null);
      setCaptionText('');
      alert('Caption updated successfully!');
    } catch (error) {
      console.error('Failed to update caption:', error);
      alert('Failed to update caption. Please try again.');
    }
  };

  const startEditCaption = (image: GalleryImage) => {
    setEditingCaption(image.key);
    setCaptionText(image.caption || '');
  };

  const cancelEditCaption = () => {
    setEditingCaption(null);
    setCaptionText('');
  };

  const handleDeleteImage = (imageKey: string) => {
    setDeleteConfirm({ type: 'image', item: imageKey });
  };

  const handleDeleteFolder = (folderName: string) => {
    setDeleteConfirm({ type: 'folder', item: folderName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      if (deleteConfirm.type === 'image') {
        await apiService.deleteGalleryImage(deleteConfirm.item);
        await loadImages();
        alert('Image deleted successfully!');
      } else {
        await apiService.deleteGalleryFolder(deleteConfirm.item);
        await loadFolders();
        if (selectedFolder === deleteConfirm.item) {
          setSelectedFolder('');
        }
        alert('Folder deleted successfully!');
      }
    } catch (error) {
      console.error('Failed to delete:', error);
      alert('Failed to delete. Please try again.');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Header with folder selection and actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            Gallery Management
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowCreateFolder(true)}
              className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              New Folder
            </button>
          </div>
        </div>

        {/* Folder selection */}
        <div className="flex items-center space-x-4 mb-4">
          <label className="text-sm font-medium text-gray-700">Folder:</label>
          <select
            value={selectedFolder}
            onChange={(e) => setSelectedFolder(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-church-blue"
          >
            <option value="">Root Gallery</option>
            {folders.map((folder) => (
              <option key={folder} value={folder}>
                {folder}
              </option>
            ))}
          </select>
          
          {selectedFolder && (
            <button
              onClick={() => handleDeleteFolder(selectedFolder)}
              className="text-red-600 hover:text-red-800 px-2 py-1 text-sm"
              title="Delete folder"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Upload section */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
          <div className="mb-4">
            <ExpiryDatePicker value={expiryDate} onChange={setExpiryDate} />
          </div>
          <div className="text-center">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploading}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className={`cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                uploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-church-blue hover:bg-blue-700'
              }`}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Image'}
            </label>
            <p className="text-sm text-gray-500 mt-2">
              Select images to upload to {selectedFolder || 'root gallery'}
              <br />
              Maximum file size: 10MB
            </p>
          </div>
        </div>
      </div>

      {/* Images grid */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">
          Images in {selectedFolder || 'Root Gallery'} ({images.length})
        </h4>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-blue mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading images...</p>
          </div>
        ) : images.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No images in this folder. Upload some images to get started!
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image) => (
              <div key={image.key} className="border border-gray-200 rounded-lg overflow-hidden">
                <img
                  src={image.url}
                  alt={image.fileName}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <h5 className="font-medium text-gray-900 truncate" title={image.fileName}>
                    {image.fileName}
                  </h5>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(image.size)} • {new Date(image.lastModified).toLocaleDateString()}
                  </p>
                  
                  {/* Caption section */}
                  <div className="mt-3">
                    {editingCaption === image.key ? (
                      <div className="space-y-2">
                        <textarea
                          value={captionText}
                          onChange={(e) => setCaptionText(e.target.value)}
                          placeholder="Enter image caption..."
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-church-blue"
                          rows={2}
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleUpdateCaption(image.key)}
                            className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditCaption}
                            className="bg-gray-300 text-gray-700 px-2 py-1 rounded text-xs hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-600 mb-2">
                          {image.caption || 'No caption'}
                        </p>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => startEditCaption(image)}
                            className="text-church-blue hover:text-blue-700 text-xs flex items-center"
                          >
                            <PencilIcon className="h-3 w-3 mr-1" />
                            Edit Caption
                          </button>
                          <button
                            onClick={() => handleDeleteImage(image.key)}
                            className="text-red-600 hover:text-red-800 text-xs flex items-center"
                          >
                            <TrashIcon className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Create New Folder</h3>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Enter folder name"
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-church-blue"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
            <div className="flex space-x-3">
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                className="flex-1 bg-church-blue text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Folder
              </button>
              <button
                onClick={() => {
                  setShowCreateFolder(false);
                  setNewFolderName('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <XMarkIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Deletion
              </h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this {deleteConfirm.type}? 
              {deleteConfirm.type === 'folder' && ' All images in this folder will also be deleted.'}
              This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;